import React, { RefObject } from 'react';
import cs from 'classnames';
import uuidv4 from 'uuid/v4';
import { Template } from '../../../../../../types/api';
import { injectV2 } from '../../../../injectV2';
import { usePreventFocusOnClick } from '../../../../hooks/usePreventFocusOnClick';
import { UiLoggerStore } from '../../../../stores/UiLoggerStore';
import { getEditorLink, getViewerLink } from '../../../../../utils/templateLinks';
import { RoutingStore } from '../../../../stores/RoutingStore';
import { BaseURLStore } from '../../../../stores/BaseURLStore';
import { ConfigStore } from '../../../../stores/ConfigStore';
import { ExperimentsStore } from '../../../../../stores/ExperimentsStore';
import { WithTranslations, withTranslations } from '../../../../../utils/withTranslations';
import { hoverPanelDataHooks } from './HoverPanel.dataHooks';
import s from './HoverPanel.scss';

interface HoverPanelProps extends WithTranslations {
  uiLoggerStore: UiLoggerStore;
  routingStore: RoutingStore;
  baseURLStore: BaseURLStore;
  configStore: ConfigStore;
  experimentsStore: ExperimentsStore;
  template: Template;
  galleryDocIndex: number;
  showInfoButtonRef: RefObject<HTMLLabelElement>;
  onShowPanel: () => void;
  openModal: () => void;
  className?: string;
  dataHook?: string;
  checkboxId: string;
  previewButtonRef: React.RefObject<HTMLAnchorElement>;
  categorySlug: string | 'search' | null;
  subCategorySlug: string | null;
}

const HoverPanelCmp: React.FC<HoverPanelProps> = (props) => {
  const {
    template,
    t,
    onShowPanel,
    galleryDocIndex,
    checkboxId,
    uiLoggerStore,
    baseURLStore,
    routingStore,
    configStore,
    className,
    dataHook,
    showInfoButtonRef,
    openModal,
    previewButtonRef,
    categorySlug,
    subCategorySlug,
    experimentsStore,
  } = props;
  const [editorSessionId, setEditorSessionId] = React.useState<string>(uuidv4());
  const { siteId, metaSiteId } = template;
  const originUrl = baseURLStore.buildURL(routingStore.location);
  const isUseMetaSiteCreator = experimentsStore.is('specs.funnel.UseMetaSiteCreator', 'true');
  const isNewIntroEnableAppSuggestions = experimentsStore.is('specs.funnel.NewIntroEnableAppSuggestions', 'true');
  const editorLink = getEditorLink({ template, editorSessionId, isUseMetaSiteCreator, isNewIntroEnableAppSuggestions });
  const viewerLink = getViewerLink({ template, language: configStore.config.currentLanguage, originUrl });

  const handleEditButtonClick = (event: React.MouseEvent) => {
    uiLoggerStore.logEditTemplate({
      siteId,
      galleryDocIndex,
      openingBrowserUrl: editorLink,
      metaSiteId,
      editorSessionId,
      hasVideo: !!template.videoSrc,
      origin: 'create-page' as const,
      category: categorySlug,
      subCategory: subCategorySlug,
    });

    setTimeout(() => generateEditorSessionId());
  };
  const generateEditorSessionId = () => {
    setEditorSessionId(uuidv4());
  };

  const handlePreviewClick = (event: React.MouseEvent) => {
    event.preventDefault();
    openModal();
  };

  const handleShowInfoKeyPress = (event: React.KeyboardEvent) => {
    if (['Enter', 'Space'].includes(event.nativeEvent.code)) {
      event.preventDefault();
      onShowPanel();
    }
  };

  const preventFocusOnClick = usePreventFocusOnClick();

  return (
    <div className={cs(s.hoverPanel, className)} data-hook={dataHook}>
      <a
        href={editorLink}
        target="_blank"
        className={s.editBtn}
        data-hook={hoverPanelDataHooks.editorLink()}
        onClick={handleEditButtonClick}
        onFocus={preventFocusOnClick}
      >
        {t('template.edit')}
      </a>
      <a
        href={viewerLink}
        target="_blank"
        className={s.previewBtn}
        data-hook={hoverPanelDataHooks.previewLink()}
        onClick={handlePreviewClick}
        onFocus={preventFocusOnClick}
        ref={previewButtonRef}
        aria-haspopup="true"
      >
        {t('template.view')}
      </a>
      <label
        htmlFor={checkboxId}
        className={s.showInfo}
        data-hook={hoverPanelDataHooks.showIconLabel()}
        aria-label={t('template.showInfo')}
        role="button"
        ref={showInfoButtonRef}
        title={t('template.showInfo')}
        onKeyDown={handleShowInfoKeyPress}
        tabIndex={0}
        onFocus={preventFocusOnClick}
      >
        {t('template.info')}
      </label>
    </div>
  );
};

export const HoverPanel = withTranslations(
  injectV2('uiLoggerStore', 'routingStore', 'baseURLStore', 'configStore', 'experimentsStore')(HoverPanelCmp),
);
